import { Platform } from 'react-native';

import { type logger as garnishLogger } from '@garnish/logger';
import { type MachineImplementationsFrom } from 'xstate5';

import { datadog } from '@sg/expo-datadog-client';

import { type otaMachine } from '../ota-machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the OTA machine's external actions so that every consumer reports the
 * same update lifecycle.
 */
export const createOtaMachineReporter = (params: Params): OtaMachineActions => {
  const { logger } = params;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const report = (params: ReportParams) => {
    const { event, level = 'info', attributes } = params;

    if (Platform.OS === 'web') return;

    const datadogMessage = [`[OTA]`, event].join(' ');

    logger.debug(event);

    try {
      datadog.logger[level](datadogMessage, { event: `ota.${event}`, ...attributes });
      void datadog.addAction(datadogMessage, attributes);
    } catch {}
  };

  const onLaunchReconciled: LaunchOutcomeReporter = ({ context }) => {
    const { launchOutcome } = context;

    if (launchOutcome.kind === 'update-applied') {
      report({ event: 'update_applied', attributes: launchOutcome });
    }

    if (launchOutcome.kind === 'install-failed') {
      report({ event: 'update_install_failed', level: 'warn', attributes: launchOutcome });
    }
  };

  // ─────────────────────────────────────────────────────────────────────

  return {
    onLaunchReconciled,
    onLaunchGateEntered: () => report({ event: 'launch_gate_entered' }),
    onLaunchGateTimedOut: () => report({ event: 'launch_gate_timed_out', level: 'warn' }),
    onLaunchFetchStarted: () => report({ event: 'launch_fetch_started' }),
    onLaunchFetchNoUpdate: () => report({ event: 'launch_fetch_no_update' }),
    onLaunchFetchUpdateFound: () => report({ event: 'launch_fetch_update_found' }),
    onLaunchFetchFailed: () => report({ event: 'launch_fetch_failed', level: 'warn' }),
    onLaunchFetchTimedOut: () => report({ event: 'launch_fetch_timed_out', level: 'warn' }),
    onUpdateFound: () => report({ event: 'update_found' }),
    onRestartDeferred: () => report({ event: 'restart_deferred' }),
    onRestartStarted: () => report({ event: 'restart_started' }),
    onRestartFailed: () => report({ event: 'restart_failed', level: 'error' }),
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  logger: ReturnType<typeof garnishLogger.extend>;
};

type ReportParams = {
  event: string;
  level?: keyof typeof datadog.logger;
  attributes?: Record<string, unknown>;
};

type OtaMachineActions = NonNullable<MachineImplementationsFrom<typeof otaMachine>['actions']>;

type LaunchOutcomeReporter = OtaMachineActions['onLaunchReconciled'];
